import {ChatInput, type ChatInputProps} from '@github-ui/copilot-chat/components/ChatInput'
import {queryForRepos, TaskChatInputToolbar} from '@github-ui/copilot-chat/components/TaskChatInputToolbar'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useChatStateValue} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import type {IntentDetectionMode} from '../../types/intent-detection-mode'
import {
  CopilotPlan,
  ModeSupportName,
  type CopilotChatReference,
  type CopilotChatRepo,
  type LineRange,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {useLastUsedRepository} from '@github-ui/copilot-chat/hooks/use-last-used-repository'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {AgentIcon} from '@primer/octicons-react'
import type {Repository} from '@github-ui/current-repository'
import {useSearchParams} from '@github-ui/use-navigate'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {ModelPicker} from '../task-view/ModelPicker'
import {useAgentModels} from '../../hooks/useAgentModels'
import {agentSupportsModelSelection, isCopilotCodingAgent} from '../../utils/agent-helpers'
import {useLastSelectedAgent} from '../../hooks/use-last-selected-agent'
import {useAgentVendorModel} from '../../hooks/use-agent-vendor-model'
import {EnvironmentPicker} from './environment-strip/EnvironmentPicker'
import type {EnvironmentOption} from './environment-strip/environment-option'
import type {StartLocalDeviceSessionInput} from '../../hooks/use-create-local-device-session'

import {makeFileReference, makeSnippetReference} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {SelectedAgent} from '../../types/session'
import {useIntentDetection} from '../../hooks/useIntentDetection'
import {useMarkdownImageHandling} from '../../hooks/use-markdown-image-handling'
import {useImageReferenceSync} from '../../hooks/use-image-reference-sync'
import {clsx} from 'clsx'
import styles from './NewTaskChatInput.module.css'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ModeSelector} from './ModeSelector'
import {ImageAttachButton} from './ImageAttachButton'
import {EnvironmentStrip} from './environment-strip/EnvironmentStrip'
import {AgentSelector} from './AgentSelector'
import {ConsecutiveCAPI401sProvider} from '../../contexts/ConsecutiveCAPI401sContext'
import {useIntentDetectionContext} from '../../contexts/IntentDetectionContext'
import {CHRONICLE_COMMANDS} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {MAX_AGENT_TASK_PROMPT_LENGTH} from '../../utils/constants'
import type {AgentQuotaSnapshot} from '../../types/agent-quota-snapshot'
import {QuotaBanner} from '../task-view/QuotaBanner'
import {useChronicleAutocomplete} from '../../hooks/use-chronicle-autocomplete'
import {ChronicleAutocomplete} from '../chronicle-autocomplete/ChronicleAutocomplete'

export function getDraftStorageKeySuffix(eventType: string | undefined, repoNameWithOwner: string | undefined): string {
  const base = eventType ?? ''
  return repoNameWithOwner ? `${base}:${repoNameWithOwner}` : base
}

/**
 * Formats snippet/file references as context to append to the problem statement.
 * This ensures the agent session receives the file/line context the user highlighted.
 */
function getReferenceContextLines(references: CopilotChatReference[]): string[] {
  const codeRefs = references.filter(
    (ref): ref is CopilotChatReference & {type: 'snippet' | 'file' | 'file-v2'} =>
      ref.type === 'snippet' || ref.type === 'file' || ref.type === 'file-v2',
  )
  if (codeRefs.length === 0) return []

  return codeRefs.map(ref => {
    let url = ref.url
    if (ref.type === 'snippet' && ref.range) {
      const hash = `#L${ref.range.start}-L${ref.range.end}`
      // Append line range hash if not already present
      if (!url.includes('#L')) {
        url = `${url}${hash}`
      }
    }
    return url
  })
}

function buildPromptWithReferenceContext({
  content,
  references,
  maxPromptLength,
}: {
  content: string
  references: CopilotChatReference[]
  maxPromptLength: number
}): string {
  const contentWithinLimit = content.slice(0, maxPromptLength)
  if (content.length >= maxPromptLength) return contentWithinLimit

  const referenceLines = getReferenceContextLines(references)
  if (referenceLines.length === 0) return contentWithinLimit

  const remainingChars = maxPromptLength - contentWithinLimit.length
  const contextHeader = '\n\nReferenced code:\n'
  if (remainingChars <= contextHeader.length) return contentWithinLimit

  let referenceContext = contextHeader
  for (const line of referenceLines) {
    const separator = referenceContext === contextHeader ? '' : '\n'
    const nextContext = `${referenceContext}${separator}${line}`
    if (nextContext.length > remainingChars) break
    referenceContext = nextContext
  }

  if (referenceContext === contextHeader) return contentWithinLimit
  return `${contentWithinLimit}${referenceContext}`
}

export type CreateTaskReference = {
  path: string
  range?: LineRange
}

function makeFileOrSnippetReference({
  referenceRepo,
  reference,
}: {
  referenceRepo: CopilotChatRepo
  reference: CreateTaskReference
}) {
  if (reference.range) {
    return makeSnippetReference(reference.path, reference.range, referenceRepo)
  } else {
    return makeFileReference(reference.path, referenceRepo)
  }
}

/**
 * Unified hook for all image-related concerns in the new-task chat input:
 * paste/drop upload handling, markdown→reference syncing, and image-limit tracking.
 */
function useMarkdownImages({
  textAreaRef,
  repositoryId,
}: {
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  repositoryId?: string
}) {
  const {onPaste, dropTargetProps, isDraggedOver, isUploading} = useMarkdownImageHandling({
    textAreaRef,
    repositoryId,
  })

  const manager = useChatManager()
  const currentReferences = useChatStateValue('currentReferences')
  const [inputText, setInputText] = useState('')

  const {isAtImageLimit} = useImageReferenceSync({
    text: inputText,
    manager,
    currentReferences,
    enabled: true,
  })

  return {
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isUploading,
    isAtImageLimit,
    onTextChange: setInputText,
  }
}

export type NewTaskChatInputProps = {
  handleUserSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    selectedMode?: IntentDetectionMode,
    modelId?: string,
    createPullRequest?: boolean,
  ) => Promise<void>
  initialRepo?: Repository | null
  initialReference?: CreateTaskReference | null
  hideRepoSelector?: boolean
  isInPortal?: ChatInputProps['isInPortal']
  textAreaRef?: ChatInputProps['textAreaRef']
  chatInputRef?: ChatInputProps['chatInputRef']
  size?: 'small' | 'large'
  className?: string
  eventType?: string
  shouldClearTextOnSubmit?: boolean
  initialText?: string
  initialBranch?: string
  isNewConversation?: boolean
  maxTextareaHeight?: number
  submitWithModifier?: boolean
  /**
   * Called when the user submits a prompt while a local device environment
   * is selected. When omitted, the local environment option
   * is hidden / unsubmittable.
   */
  onLocalDeviceSubmit?: (input: StartLocalDeviceSessionInput) => Promise<unknown>
  /**
   * Called when the user submits a prompt while an AHP environment is selected.
   * When omitted, the environment options are still shown but submit falls back
   * to the default cloud path.
   */
  onEnvironmentSubmit?: (input: {environmentId: string; prompt: string}) => Promise<unknown>
  agentQuotaSnapshot?: AgentQuotaSnapshot
}

export function NewTaskChatInput({
  handleUserSubmit,
  initialRepo,
  initialReference,
  hideRepoSelector,
  isInPortal,
  chatInputRef,
  textAreaRef: textAreaRefFromProps,
  size = 'large',
  className,
  eventType,
  shouldClearTextOnSubmit = true,
  initialText,
  initialBranch,
  isNewConversation,
  maxTextareaHeight,
  submitWithModifier = true,
  onLocalDeviceSubmit,
  onEnvironmentSubmit,
  agentQuotaSnapshot,
}: NewTaskChatInputProps) {
  // Use the chat manager to clear references when a new task is started
  const manager = useChatManager()
  const currentReferences = useChatStateValue('currentReferences')
  const {plan, tokenBasedBillingEnabled: isTokenBasedBillingEnabled} = useEntitlement()
  const {models: availableModels} = useAgentModels()

  const [lastUsedRepo, setLastUsedRepo] = useLastUsedRepository()
  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(null)

  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
    textAreaRefFromProps ?? null,
    () => textAreaRef.current,
  )

  const {
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isUploading: isImageUploading,
    isAtImageLimit,
    onTextChange,
  } = useMarkdownImages({
    textAreaRef,
    repositoryId: selectedRepo?.id?.toString(),
  })

  const [currentPrompt, setCurrentPrompt] = useState(initialText || '')
  const allChronSuggestions = useChronicleAutocomplete(currentPrompt)
  const [chronicleDismissed, setChronicleDismissed] = useState(false)

  // Re-show suggestions when input changes
  const handleTextChange = useCallback(
    (text: string) => {
      onTextChange(text)
      setCurrentPrompt(text)
      setChronicleDismissed(false)
    },
    [onTextChange],
  )

  const handleChronicleSelect = useCallback(
    (command: string) => {
      setCurrentPrompt(command)
      onTextChange(command)
      if (chatInputRef?.current) {
        chatInputRef.current.setText(command)
      }
    },
    [onTextChange, chatInputRef],
  )

  const [referenceRepo, setReferenceRepo] = useState<CopilotChatRepo | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string>(initialBranch || '')
  const [searchParams] = useSearchParams()
  const agentParam = searchParams.get('agent')
  const preselectedAgentFromParam: SelectedAgent | undefined = agentParam
    ? {typeName: 'customSubAgent', subAgentSlug: agentParam}
    : undefined
  const [selectedAgent, setSelectedAgent] = useLastSelectedAgent(preselectedAgentFromParam)

  const showEnvironmentStrip = isFeatureEnabled('coding_agent_environment_strip')
  const isSubAgentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')
  const {isUsingIntentDetection} = useIntentDetection()
  const {mode: selectedMode, setMode: setSelectedMode} = useIntentDetectionContext()

  const {agentSlug, vendorFilters, selectedModelId, setSelectedModelId} = useAgentVendorModel(selectedAgent)

  // No topLevelAgentId means the default Copilot agent is selected (empty SelectedAgent object)
  const isCopilotAgent = !selectedAgent.topLevelAgentId || isCopilotCodingAgent(agentSlug)
  const chronicleSuggestions = isCopilotAgent && !chronicleDismissed ? allChronSuggestions : []

  const shouldShowModelPicker =
    copilotFeatureFlags.codingAgentModelSelection &&
    (copilotFeatureFlags.codingAgentModelSelectionAllSkus ||
      plan === CopilotPlan.IndividualPro ||
      plan === CopilotPlan.IndividualProPlus ||
      plan === CopilotPlan.IndividualMax ||
      plan === CopilotPlan.IndividualStudent) &&
    agentSupportsModelSelection(selectedAgent, agentSlug)

  const [reference, setReference] = useState<CreateTaskReference | null>(initialReference || null)
  const [selectedEnvironment, setSelectedEnvironment] = useState<EnvironmentOption | null>(null)
  const handleSelectedEnvironmentUnavailable = useCallback(() => setSelectedEnvironment(null), [])
  const isRemoteKickstartEnabled = isFeatureEnabled('copilot_mission_control_remote_kickstart')
  const isEnvironmentsEnabled = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  // When a local device or AHP environment is the active target, the cloud-only
  // controls (repo, branch, agent, image upload, model picker) are not relevant
  // — they're hidden so the only required input is the prompt itself.
  const isLocalEnvironment = isRemoteKickstartEnabled && selectedEnvironment?.kind === 'local'
  const isAhpEnvironment = isEnvironmentsEnabled && selectedEnvironment?.kind === 'ahp'
  const showEnvironmentPicker =
    (isRemoteKickstartEnabled && !!onLocalDeviceSubmit) || (isEnvironmentsEnabled && !!onEnvironmentSubmit)
  const chatReference = useMemo(
    () => (referenceRepo && reference ? makeFileOrSnippetReference({reference, referenceRepo}) : undefined),
    [referenceRepo, reference],
  )
  const branchParam = searchParams.get('branch')
  const isBlockedByQuota =
    agentQuotaSnapshot?.can_start_session === false && copilotFeatureFlags.codingAgentTBBQuotaBanner

  const isValidRepo = initialRepo && Object.keys(initialRepo).length > 0
  const nwoParam = searchParams.get('repo')
  let initialNameWithOwner = ''
  if (isValidRepo) {
    initialNameWithOwner = `${initialRepo.ownerLogin}/${initialRepo.name}`
  } else if (nwoParam) {
    initialNameWithOwner = nwoParam
  } else if (lastUsedRepo) {
    initialNameWithOwner = `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`
  }

  const onSubmit = useCallback(
    async (content: string) => {
      sendEvent('agent_sessions.task_view', {
        target: 'NEW_TASK_SUBMIT',
        defaultToTask: copilotFeatureFlags.defaultToAgentInput,
        defaultToTaskControl: copilotFeatureFlags.defaultToAgentControl,
        askModeDropdown: copilotFeatureFlags.askModeDropdown,
      })

      // Save the selected repository to localStorage when task is created
      if (selectedRepo?.id != null) {
        const result = await manager.service.fetchRepo(selectedRepo.id)
        if (result.ok && result.payload) {
          setLastUsedRepo(result.payload)
        }
      }

      const modelIdToUse =
        selectedModelId ?? availableModels?.find(m => m.is_chat_default)?.id ?? availableModels?.[0]?.id

      // Append file/snippet reference context to the prompt so the agent session
      // has visibility into what the user highlighted when creating the task.
      const contentWithReferences = buildPromptWithReferenceContext({
        content,
        references: currentReferences,
        maxPromptLength: MAX_AGENT_TASK_PROMPT_LENGTH,
      })

      if (
        selectedEnvironment?.kind === 'local' &&
        selectedEnvironment.deviceId &&
        selectedEnvironment.workingDirectory &&
        onLocalDeviceSubmit
      ) {
        try {
          await onLocalDeviceSubmit({
            deviceId: selectedEnvironment.deviceId,
            workingDirectory: selectedEnvironment.workingDirectory,
            problemStatement: contentWithReferences,
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to dispatch task to local device', error)
        }
        return
      }

      // AHP environment target: dispatch the prompt to the Mission Control
      // environment over the relay, then navigate to its event stream.
      if (selectedEnvironment?.kind === 'ahp' && selectedEnvironment.environmentId && onEnvironmentSubmit) {
        try {
          await onEnvironmentSubmit({
            environmentId: selectedEnvironment.environmentId,
            prompt: contentWithReferences,
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to dispatch task to environment', error)
        }
        return
      }

      return handleUserSubmit(
        contentWithReferences,
        selectedRepo,
        selectedBranch,
        selectedAgent,
        selectedMode,
        modelIdToUse,
      )
    },
    [
      selectedRepo,
      selectedModelId,
      availableModels,
      selectedEnvironment,
      onLocalDeviceSubmit,
      onEnvironmentSubmit,
      handleUserSubmit,
      selectedBranch,
      selectedAgent,
      selectedMode,
      manager.service,
      setLastUsedRepo,
      currentReferences,
    ],
  )

  const handleRepoSelect = useCallback((repo: DropdownRepository | undefined) => {
    if (!repo) return

    setSelectedRepo(repo)
    setSelectedBranch(repo.defaultBranch || '')
  }, [])

  // Fetch the reference repo
  useEffect(() => {
    if (initialRepo?.id != null) {
      ;(async () => {
        const repoResponse = await manager.service.fetchRepo(initialRepo.id)
        if (repoResponse.payload) setReferenceRepo(repoResponse.payload)
      })()
    }
  }, [initialRepo, manager])

  useEffect(() => {
    // If the repo selector is hidden and we have an initial repo NWO, try to auto-select it.
    if (hideRepoSelector && initialNameWithOwner) {
      const fetchInitialRepo = async () => {
        try {
          const repositories = await queryForRepos(initialNameWithOwner)
          const repo = repositories.find(
            (repoData: {nameWithOwner: string}) => repoData.nameWithOwner === initialNameWithOwner,
          )
          if (repo && repo.enabled) {
            setSelectedRepo(repo)
            setSelectedBranch(initialBranch ?? branchParam ?? repo.defaultBranch)
            return
          }
        } catch {
          // Error querying for specific repo
        }
      }
      fetchInitialRepo()
    }
    // We only want this hook to run once since it's fetching the initial repo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={styles.inputWrapper}>
      <QuotaBanner agentQuotaSnapshot={agentQuotaSnapshot} />
      <div className={styles.autocompleteWrapper}>
        <ChronicleAutocomplete
          suggestions={chronicleSuggestions}
          onSelect={handleChronicleSelect}
          onDismiss={() => setChronicleDismissed(true)}
          textAreaRef={textAreaRef}
          currentText={currentPrompt}
        />
        <ChatInput
          highlightCommands={isCopilotAgent ? [...CHRONICLE_COMMANDS] : undefined}
          initialText={initialText}
          className={clsx(className, {[styles.isDraggedOver]: isDraggedOver})}
          modes={[
            {
              name: ModeSupportName.Agent,
              icon: AgentIcon,
              submitLabel: isUsingIntentDetection ? 'Send now' : 'Start task',
              placeholder: isLocalEnvironment
                ? 'Kick off a remote session with the Copilot CLI'
                : isUsingIntentDetection
                  ? 'Ask anything with intent'
                  : 'Give Copilot a background task to work on',
              textAreaAriaLabel: isUsingIntentDetection ? 'Ask anything with intent' : 'Coding task prompt',
              supportsAttachments: false,
              supportsReferenceAttachments: true,
              supportedReferenceTypes: ['image', 'snippet', 'file'],
              supportsModelSwitching: shouldShowModelPicker,
              supportsAttachmentsMenu: false,
            },
          ]}
          size={size}
          onSubmit={onSubmit}
          shouldSubmitWithModifier={submitWithModifier}
          isNewConversation={isNewConversation}
          initialReference={chatReference}
          extraToolbar={
            <>
              {showEnvironmentPicker && !showEnvironmentStrip && (
                <EnvironmentPicker
                  selectedEnvironmentId={selectedEnvironment?.id}
                  onSelectEnvironment={setSelectedEnvironment}
                  onSelectedEnvironmentUnavailable={handleSelectedEnvironmentUnavailable}
                />
              )}
              {!isLocalEnvironment && !isAhpEnvironment && (
                <>
                  {isUsingIntentDetection && <ModeSelector selectedMode={selectedMode} setMode={setSelectedMode} />}
                  {showEnvironmentStrip ? (
                    <div className={styles.extraToolbarActions}>
                      <ImageAttachButton
                        textAreaRef={textAreaRef}
                        repositoryId={selectedRepo?.id?.toString()}
                        disabled={!selectedRepo}
                        isAtImageLimit={isAtImageLimit}
                      />
                      {isSubAgentsEnabled && selectedRepo && (
                        // AgentSelector -> useSubAgents -> useCAPIQuery requires ConsecutiveCAPI401sProvider.
                        // The non-env-strip branch gets this provider from TaskChatInputToolbar, but this branch
                        // renders AgentSelector directly, so we wrap it here. AgentsView already provides one
                        // higher in the tree, but CopilotChatInputPartial and copilot-immersive-v1's
                        // TasksInChatInput do not, so this wrap is load-bearing for those consumers.
                        <ConsecutiveCAPI401sProvider>
                          <AgentSelector
                            repo={selectedRepo}
                            branch={selectedBranch || selectedRepo.defaultBranch}
                            selectedAgent={selectedAgent}
                            onSelectAgent={(agent: SelectedAgent) => setSelectedAgent(agent)}
                            iconOnly
                            buttonProps={{variant: 'invisible', size: 'small'}}
                          />
                        </ConsecutiveCAPI401sProvider>
                      )}
                    </div>
                  ) : (
                    <TaskChatInputToolbar
                      hideRepoSelector={hideRepoSelector}
                      initialRepoNWO={initialNameWithOwner}
                      initialBranch={initialBranch ?? branchParam ?? undefined}
                      selectedRepo={selectedRepo || undefined}
                      selectedBranch={selectedBranch}
                      selectedAgent={selectedAgent}
                      onRepoSelect={handleRepoSelect}
                      onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
                      onAgentSelect={(agent: SelectedAgent) => setSelectedAgent(agent)}
                      agentSelectionEnabled={isSubAgentsEnabled}
                      textAreaRef={textAreaRef}
                      isAtImageLimit={isAtImageLimit}
                      currentPrompt={currentPrompt}
                    />
                  )}
                </>
              )}
            </>
          }
          submitDisabled={
            isBlockedByQuota ||
            (isLocalEnvironment
              ? !onLocalDeviceSubmit || !(selectedEnvironment?.deviceId && selectedEnvironment.workingDirectory)
              : isAhpEnvironment
                ? !onEnvironmentSubmit || !selectedEnvironment?.environmentId
                : !selectedRepo || isImageUploading)
          }
          disabled={isBlockedByQuota}
          onRemoveReference={() => {
            setReference(null)
          }}
          isInPortal={isInPortal}
          textAreaRef={textAreaRef}
          chatInputRef={chatInputRef}
          onPaste={isLocalEnvironment ? undefined : onPaste}
          dropTargetProps={isLocalEnvironment ? undefined : dropTargetProps}
          onTextChange={handleTextChange}
          // Intentionally passing an empty function to onNewThreadSelected to enable the model picker UI.
          // ChatInput requires this prop to show the model picker, even if thread creation is not needed here.
          onNewThreadSelected={async () => {}}
          customModelPicker={
            isLocalEnvironment || isAhpEnvironment
              ? () => null
              : shouldShowModelPicker
                ? () => (
                    <ModelPicker
                      selectedModelId={selectedModelId}
                      onUpdateModel={setSelectedModelId}
                      vendorFilters={vendorFilters}
                      isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    />
                  )
                : () => null
          }
          storageKeySuffix={getDraftStorageKeySuffix(eventType, selectedRepo?.nameWithOwner ?? initialNameWithOwner)}
          source="task"
          showStartTaskButton={copilotFeatureFlags.defaultToAgentInput}
          shouldClearTextOnSubmit={shouldClearTextOnSubmit}
          maxTextareaHeight={maxTextareaHeight}
          maxPromptLength={MAX_AGENT_TASK_PROMPT_LENGTH}
          enableAutoCorrect
        />
      </div>
      {showEnvironmentStrip && (
        <EnvironmentStrip
          hideRepoSelector={hideRepoSelector}
          initialRepoNWO={initialNameWithOwner}
          initialBranch={initialBranch ?? branchParam ?? undefined}
          selectedRepo={selectedRepo || undefined}
          selectedBranch={selectedBranch}
          onRepoSelect={handleRepoSelect}
          onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
          currentPrompt={currentPrompt}
          selectedEnvironment={selectedEnvironment}
          onSelectEnvironment={setSelectedEnvironment}
          onSelectedEnvironmentUnavailable={handleSelectedEnvironmentUnavailable}
        />
      )}
    </div>
  )
}
